/**
 * Analysis Animation Route - Dedicated path for active analysis
 *
 * URL Structure:
 * - /analyze/{tempId}?mode=theatrical
 * - /analyze/{tempId}?mode=quick
 * - /analyze/{tempId}?mode=dramax_premium
 *
 * Features:
 * - Clean path-based routing (no ?phase= query param)
 * - Shareable animation links with mode preserved
 * - Browser back button support
 * - Deep linking from mobile
 * - Automatic transition to results when complete
 *
 * Flow:
 * 1. User submits text → Navigate to /analyze/{tempId}?mode={variant}
 * 2. Animation plays with selected theatrical mode
 * 3. On complete → Navigate to /results/{analysisId}?mode={variant}
 *
 * @see docs/URL_ROUTING_IMPLEMENTATION.md
 */

import { useEffect, useState, useCallback } from "react";
import { View, StyleSheet, Platform } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { LinearGradient } from "expo-linear-gradient";
import { TierBody } from "@dramadetector/ui";
import { useTranslation } from "@dramadetector/shared";
import { debugLog, criticalLog } from "../../../lib/debug-logger";

// Animation components
import {
  LetterThrowingAnimation,
  type SpeedPreset,
  type AnimationVariant,
} from "../../../components/letter-throwing";
import { getCategoryFromScore } from "../../../components/modern-results";

// Skip to Results - minimal waiting experience for quick mode
import { SkipToResultsLoader } from "../../../components/skip-to-results";
import { useSkipToResults } from "../../../hooks/useSkipToResults";

// Theatrical mode
import { useTheatricalMode } from "../../../hooks/useTheatricalMode";
import { TheatricalModeChip } from "../../../components/theatrical/TheatricalModeChip";
import type { TheatricalModeVariant } from "../../../lib/theatrical/theatricalModeConfig";

// Hooks
import { captureEvent } from "../../../lib/analytics";
import { useAnalysisStore } from "../../../store/analysis";
import { useEntitlements } from "../../../hooks/useEntitlements";


// Error boundary
import { AnimationErrorBoundary } from "../../../components/ErrorBoundary";
import { OfflineBanner } from "../../../components/OfflineBanner";

// Hardcoded animation settings
const ANIMATION_SPEED: SpeedPreset = "1.25";
const ANIMATION_VARIANT: AnimationVariant = "letterZoom";

/**
 * Extract score and category from analysis result.
 * Handles both flat and nested (unified pipeline) response structures.
 */
function extractAnalysisResult(
  result: Record<string, unknown> | undefined
): { score: number; category: string } | undefined {
  if (!result) return undefined;

  // Unified pipeline uses nested `result` structure
  const nestedResult = result.result as Record<string, unknown> | undefined;
  const score = (nestedResult?.score ?? result.score ?? 0) as number;
  const category =
    (nestedResult?.category ?? result.category ?? getCategoryFromScore(score)) as string;

  return { score, category };
}

export default function AnalyzeScreen() {
  debugLog('mount', 'AnalyzeScreen mounted', {});

  const { t } = useTranslation("home");
  const router = useRouter();
  const params = useLocalSearchParams<{
    id: string;
    mode?: string;
    text?: string; // Pass text via params for page refresh support
  }>();

  debugLog('mount', 'AnalyzeScreen params', { id: params.id, mode: params.mode });

  // Theatrical mode
  const { config: theatricalConfig, variant: theatricalVariant, setMode: setTheatricalMode } = useTheatricalMode();
  const effectiveTheatricalVariant = (params.mode as TheatricalModeVariant) || theatricalVariant;

  // Get analysis from Zustand store (stored by useAnalyze in index.tsx)
  // This solves the "fresh hook instance has no data" problem
  const analysisItem = useAnalysisStore((state) => state.getById(params.id || ''));
  const data = analysisItem?.result ? { data: analysisItem.result } : undefined;
  const isPending = analysisItem?.status === 'pending';
  const error = analysisItem?.status === 'error'
    ? new Error(analysisItem?.errorInfo?.message || analysisItem?.error || 'Analysis failed')
    : undefined;

  // User tier for narrator quips (determines voice style and quip set)
  const { tier } = useEntitlements();

  // Local state
  const [animationComplete, setAnimationComplete] = useState(false);
  const [hasNavigatedToResults, setHasNavigatedToResults] = useState(false);

  // Platform type for analytics
  const platformType = Platform.OS as "ios" | "android" | "web";

  // Reset navigation state when analysis ID changes (prevents stale state from previous analysis)
  useEffect(() => {
    debugLog('state', 'Analysis ID changed, resetting navigation state', {
      id: params.id,
      resetting: { animationComplete: false, hasNavigatedToResults: false }
    });
    setAnimationComplete(false);
    setHasNavigatedToResults(false);
  }, [params.id]);

  // Skip to Results - fast track for quick mode
  const isQuickMode = effectiveTheatricalVariant === 'quick';
  const { shouldShowLoader } = useSkipToResults({
    analysisId: params.id,
    isQuickMode,
    onNavigate: (analysisId) => {
      router.replace({
        pathname: "/results/[analysisId]",
        params: {
          analysisId,
          mode: effectiveTheatricalVariant,
        },
      });
    },
  });

  // analyze_screen_view retired (#797 — keep unified_analysis_view as canonical event)

  const handleAnimationComplete = useCallback(() => {
    criticalLog('animation', 'Animation completed', { id: params.id, hasData: !!data?.data });
    setAnimationComplete(true);
  }, [params.id, data]);

  const handleSkipAnimation = useCallback(() => {
    captureEvent("unified_animation_skipped", {
      speed: ANIMATION_SPEED,
      temp_id: params.id,
      theatrical_mode: effectiveTheatricalVariant,
      platform: platformType,
    });
    handleAnimationComplete();
  }, [handleAnimationComplete, params.id, effectiveTheatricalVariant, platformType]);

  // Navigate to results when both animation and API are complete
  useEffect(() => {
    debugLog('state', 'Navigation effect running', {
      animationComplete,
      hasData: !!data?.data,
      isPending,
      hasNavigatedToResults,
      isQuickMode,
    });

    if (animationComplete && data?.data && !isPending && !hasNavigatedToResults) {
      const analysisData = data.data;

      criticalLog('navigation', 'Navigating to results', {
        from: 'analyze',
        to: '/results/[analysisId]',
        analysisId: analysisData.id,
        mode: effectiveTheatricalVariant,
      });

      // Track transition to results
      captureEvent("analyze_to_results_transition", {
        temp_id: params.id,
        analysis_id: analysisData.id,
        theatrical_mode: effectiveTheatricalVariant,
        platform: platformType,
      });

      // Navigate to results page with mode preserved
      // Note: Quick mode navigation is handled by useSkipToResults hook
      if (!isQuickMode) {
        router.replace({
          pathname: "/results/[analysisId]",
          params: {
            analysisId: analysisData.id,
            mode: effectiveTheatricalVariant,
          },
        });
        setHasNavigatedToResults(true);
      }
    }
  }, [animationComplete, data, isPending, hasNavigatedToResults, params.id, effectiveTheatricalVariant, router, platformType, isQuickMode]);

  // Handle API error - navigate back to home
  useEffect(() => {
    if (error) {
      captureEvent("analyze_error", {
        temp_id: params.id,
        error_message: error.message,
        animation_complete: animationComplete,
        theatrical_mode: effectiveTheatricalVariant,
        platform: platformType,
      });

      // Navigate back to home with error
      router.replace({
        pathname: "/(tabs)",
        params: { error: error.message },
      });
    }
  }, [error, animationComplete, params.id, effectiveTheatricalVariant, router, platformType]);

  const handleModeToggle = useCallback(async () => {
    const nextMode = effectiveTheatricalVariant === 'quick' ? 'theatrical' : 'quick';
    await setTheatricalMode(nextMode, 'manual_toggle');

    // Update URL with new mode
    router.push({
      pathname: "/analyze/[id]",
      params: {
        id: params.id,
        mode: nextMode,
        text: params.text,
      },
    });
  }, [effectiveTheatricalVariant, setTheatricalMode, router, params.id, params.text]);

  // Quick mode: Show minimal loader, navigate immediately when API completes
  if (shouldShowLoader) {
    return <SkipToResultsLoader message={params.text || "Analyzing..."} />;
  }

  return (
    <View style={styles.container}>
      {/* Y2K Gradient Background */}
      <LinearGradient
        colors={["#0a0012", "#1a0a2e", "#0d1117"]}
        style={StyleSheet.absoluteFill}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      />

      {/* Offline Banner */}
      <OfflineBanner />

      <AnimationErrorBoundary
        staticFallback={
          <View style={styles.errorFallback}>
            <TierBody size="base" color="text-gray-600">
              {t("home.modern.animation_error", { defaultValue: "Animation failed. Redirecting..." })}
            </TierBody>
          </View>
        }
      >
        <LetterThrowingAnimation
          key={params.id}
          text={params.text || "Analyzing..."}
          speed={ANIMATION_SPEED}
          variant={ANIMATION_VARIANT}
          onComplete={handleAnimationComplete}
          onSkip={handleSkipAnimation}
          analysisResult={extractAnalysisResult(data?.data as unknown as Record<string, unknown>)}
          theatricalConfig={theatricalConfig}
          narratorStreamedText={
            // Unified pipeline nests data under `result`, legacy is flat
            ((data?.data as unknown as Record<string, unknown>)?.result as Record<string, unknown>)?.narrator_intro as string | undefined
            ?? (data?.data as unknown as Record<string, unknown>)?.narrator_intro as string | undefined
          }
          userTier={tier}
          autoStart
        />
        <TheatricalModeChip
          variant={effectiveTheatricalVariant}
          onToggle={handleModeToggle}
          visible={!animationComplete}
        />
      </AnimationErrorBoundary>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  errorFallback: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
});
